import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PRODUCTS } from '../content/products';
import { LINK_ARROW } from './styles';

const LOCATION_OPTIONS = [
  { key: 'front', label: 'Front of Stove' },
  { key: 'back', label: 'Rear of Stove' },
  { key: 'top', label: 'Top of Stove' },
];
const SIZE_OPTIONS = [
  { key: 'standard', label: 'Standard' },
  { key: 'compact', label: 'Compact' },
  { key: 'deluxe', label: 'Deluxe (Pro-Style)' },
];
const LOCKING_OPTIONS = [
  { key: 'classic', label: 'Classic' },
  { key: 'ultimate', label: 'Ultimate' },
  { key: 'both', label: 'Compare both' },
];
const FINISH_OPTIONS = [
  { key: 'brushed', label: 'Brushed Stainless', swatch: 'bg-gradient-to-br from-zinc-300 via-zinc-400 to-zinc-500' },
  { key: 'black', label: 'Black', swatch: 'bg-black border border-line2' },
  { key: 'fullmetal', label: 'Full Metal', swatch: 'bg-gradient-to-br from-zinc-200 via-zinc-500 to-zinc-700' },
  { key: 'all', label: 'Show all', swatch: 'bg-gradient-to-br from-zinc-300 via-zinc-500 to-zinc-300' },
];
const SIZE_MAP = { standard: 'Standard', compact: 'Compact', deluxe: 'Deluxe (Pro-Style)' };

// A knob with a Compact body works in any position (rated "Front, rear, top");
// every other knob is rated "Front" only. Rear and Top therefore need a Compact-body knob.
const productSupportsBack = (p) => p.size === 'Compact';
const matchLocation = (p, loc) => {
  if (!loc || loc === 'front') return true;
  return productSupportsBack(p);
};

const matchFinish = (p, f) =>
  !f || f === 'all' ||
  (f === 'brushed' ? p.finish.includes('Brushed') : f === 'black' ? p.finish === 'Black' : p.finish.includes('Full Metal'));
const matchSize = (p, s) => !s || p.size === SIZE_MAP[s];
const matchLocking = (p, l) =>
  !l || l === 'both' || (l === 'classic' ? p.locking === false : p.locking === true);

const chip = (active) =>
  `inline-flex items-center gap-2 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
    active ? 'border-accent bg-accent text-white' : 'border-line2 text-silver hover:border-mute'
  }`;
const chipDisabled = 'inline-flex items-center gap-2 border border-line2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mute opacity-40 cursor-not-allowed line-through';

export default function KnobFinder() {
  const [location, setLocation] = useState(null);
  const [size, setSize] = useState(null);
  const [locking, setLocking] = useState(null);
  const [finish, setFinish] = useState(null);
  const [notice, setNotice] = useState(null);

  const results = useMemo(
    () => PRODUCTS.filter((p) => matchLocation(p, location) && matchSize(p, size) && matchLocking(p, locking) && matchFinish(p, finish)),
    [location, size, locking, finish]
  );
  const anySelection = Boolean(location || size || locking || finish);
  const resultsPanelRef = useRef(null);

  // When matches render, bring the results panel into view (it sits below the
  // controls on small screens, so without this the matches appear off-screen).
  useEffect(() => {
    if (anySelection && results.length > 0 && resultsPanelRef.current) {
      resultsPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [results, anySelection]);

  const cascadeClear = (nextLocation, nextSize, nextLocking) => {
    if (!finish || finish === 'all') return false;
    const left = PRODUCTS.filter(
      (p) => matchLocation(p, nextLocation) && matchSize(p, nextSize) && matchLocking(p, nextLocking) && matchFinish(p, finish)
    );
    if (left.length === 0) {
      const label = FINISH_OPTIONS.find((o) => o.key === finish).label;
      setFinish('all');
      setNotice(`${label} isn't available with those choices — showing all finishes.`);
      return true;
    }
    return false;
  };

  const pickLocation = (key) => {
    setNotice(null);
    const next = location === key ? null : key;
    setLocation(next);
    cascadeClear(next, size, locking);
  };
  const pickSize = (key) => {
    setNotice(null);
    const next = size === key ? null : key;
    setSize(next);
    cascadeClear(location, next, locking);
  };
  const pickLocking = (key) => {
    setNotice(null);
    const next = locking === key ? null : key;
    setLocking(next);
    cascadeClear(location, size, next);
  };
  const pickFinish = (key) => {
    setNotice(null);
    setFinish(finish === key ? null : key);
  };

  const locationDisabled = (key) =>
    PRODUCTS.filter((p) => matchLocation(p, key) && matchSize(p, size) && matchLocking(p, locking) && matchFinish(p, finish)).length === 0;
  const sizeDisabled = (key) =>
    PRODUCTS.filter((p) => matchLocation(p, location) && matchSize(p, key) && matchLocking(p, locking) && matchFinish(p, finish)).length === 0;
  const lockingDisabled = (key) =>
    PRODUCTS.filter((p) => matchLocation(p, location) && matchSize(p, size) && matchLocking(p, key) && matchFinish(p, finish)).length === 0;
  const finishDisabled = (key) =>
    key !== 'all' && PRODUCTS.filter((p) => matchLocation(p, location) && matchSize(p, size) && matchLocking(p, locking) && matchFinish(p, key)).length === 0;

  const explain = (kind, key) => {
    const activeFinishLabel = finish ? FINISH_OPTIONS.find((o) => o.key === finish)?.label : null;
    const thisFinishLabel = FINISH_OPTIONS.find((o) => o.key === key)?.label;
    if (kind === 'size') {
      if (finish && finish !== 'all' && PRODUCTS.filter((p) => matchSize(p, key) && matchLocking(p, locking) && matchFinish(p, finish)).length === 0)
        return `${activeFinishLabel} isn't listed for ${SIZE_MAP[key]}`;
      return `Not offered with that locking option`;
    }
    if (kind === 'locking') {
      if (finish && finish !== 'all' && PRODUCTS.filter((p) => matchSize(p, size) && matchLocking(p, key) && matchFinish(p, finish)).length === 0)
        return `${activeFinishLabel} isn't offered with ${key === 'classic' ? 'Classic' : 'Ultimate'} locking`;
      return 'Not offered for that size';
    }
    if (size && PRODUCTS.filter((p) => matchSize(p, size) && matchLocking(p, locking) && matchFinish(p, key)).length === 0)
      return `${thisFinishLabel} isn't listed for ${SIZE_MAP[size]}`;
    return `${thisFinishLabel} isn't offered with that locking option`;
  };

  const groupLabel = 'font-mono text-[10px] uppercase tracking-[0.3em] text-mute';
  const noteLine = 'mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-mute';

  return (
    <div className="border border-line bg-panel/40" data-testid="knob-finder">
      <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
        <div className="min-w-0 p-6 sm:p-8 lg:border-r lg:border-line">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accentText">Find your RangeSafe knob</p>
        <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">
          Please select from the following choices
        </h3>

        <div className="mt-6 space-y-6">
          <div>
            <p className={groupLabel}>1 · Knob Location</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {LOCATION_OPTIONS.map((o) =>
                locationDisabled(o.key) ? (
                  <button key={o.key} type="button" className={chipDisabled} disabled data-testid={`finder-location-${o.key}`}>
                    {o.label}
                  </button>
                ) : (
                  <button key={o.key} type="button" className={chip(location === o.key)} onClick={() => pickLocation(o.key)} data-testid={`finder-location-${o.key}`}>
                    {o.label}
                  </button>
                )
              )}
            </div>
            {(location === 'back' || location === 'top') && (
              <p className={noteLine} data-testid="finder-location-note">
                Rear and top positions need the compact body — showing Compact knobs only.
              </p>
            )}
          </div>

          <div>
            <p className={groupLabel}>2 · Size</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {SIZE_OPTIONS.map((o) =>
                sizeDisabled(o.key) ? (
                  <button key={o.key} type="button" className={chipDisabled} disabled title={explain('size', o.key)} data-testid={`finder-size-${o.key}`}>
                    {o.label}
                  </button>
                ) : (
                  <button key={o.key} type="button" className={chip(size === o.key)} onClick={() => pickSize(o.key)} data-testid={`finder-size-${o.key}`}>
                    {o.label}
                  </button>
                )
              )}
            </div>
            {sizeDisabled('deluxe') && (
              <p className={noteLine} data-testid="finder-size-note">
                {explain('size', 'deluxe')} — no Deluxe model matches your other choices.
              </p>
            )}
          </div>

          <div>
            <p className={groupLabel}>3 · Locking option</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {LOCKING_OPTIONS.map((o) =>
                lockingDisabled(o.key) ? (
                  <button key={o.key} type="button" className={chipDisabled} disabled title={explain('locking', o.key)} data-testid={`finder-locking-${o.key}`}>
                    {o.label}
                  </button>
                ) : (
                  <button key={o.key} type="button" className={chip(locking === o.key)} onClick={() => pickLocking(o.key)} data-testid={`finder-locking-${o.key}`}>
                    {o.label}
                  </button>
                )
              )}
            </div>
            {lockingDisabled('classic') && lockingDisabled('ultimate') && (
              <p className={noteLine} data-testid="finder-locking-note">
                {explain('locking', 'classic')} — try a different finish or size.
              </p>
            )}
          </div>

          <div>
            <p className={groupLabel}>4 · Finish</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {FINISH_OPTIONS.map((o) =>
                finishDisabled(o.key) ? (
                  <button key={o.key} type="button" className={chipDisabled} disabled title={explain('finish', o.key)} data-testid={`finder-finish-${o.key}`}>
                    <span className={`h-3 w-3 ${o.swatch}`} aria-hidden="true" />
                    {o.label}
                  </button>
                ) : (
                  <button key={o.key} type="button" className={chip(finish === o.key)} onClick={() => pickFinish(o.key)} data-testid={`finder-finish-${o.key}`}>
                    <span className={`h-3 w-3 ${o.swatch}`} aria-hidden="true" />
                    {o.label}
                  </button>
                )
              )}
            </div>
            {FINISH_OPTIONS.filter((o) => finishDisabled(o.key)).length > 0 && (
              <p className={noteLine} data-testid="finder-finish-note">
                {FINISH_OPTIONS.filter((o) => finishDisabled(o.key))
                  .map((o) => explain('finish', o.key))
                  .join(' · ')}
              </p>
            )}
          </div>
        </div>

        {notice && (
          <p className="mt-6 border border-line2 bg-base px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-silver" data-testid="finder-notice">
            {notice}
          </p>
        )}
        </div>

        <div className="min-w-0 border-t border-line p-6 sm:p-8 lg:border-t-0" data-testid="finder-results-panel" ref={resultsPanelRef}>
          <h4 className="font-display text-lg font-bold uppercase tracking-tight text-white" data-testid="finder-results-heading">
            Matches your selections
          </h4>

          {!anySelection ? (
            <div
              className="mt-6 flex min-h-[300px] flex-col items-center justify-center border border-dashed border-line2 p-8 text-center"
              data-testid="finder-empty"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Your matches appear here</p>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-dim">
                Choose a location, size, locking option and finish
              </p>
            </div>
          ) : results.length === 1 ? (
            <Link
              to={`/products/${results[0].slug}`}
              className="group mt-6 flex h-full flex-col border border-line bg-base p-6 transition-colors hover:bg-panel"
              data-testid={`finder-card-${results[0].slug}`}
            >
              <div className="spec-frame flex h-52 items-center justify-center border border-line2">
                {results[0].image ? (
                  <img
                    src={results[0].image}
                    alt={`RangeSafe ${results[0].name} Stove Safety Knob`}
                    className="max-h-44 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                    width="768"
                    height="1024"
                  />
                ) : null}
              </div>
              <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.25em] text-accentText">
                {results[0].size} · {results[0].locking ? 'Ultimate locking' : 'Classic'}
              </p>
              <h5 className="mt-2 font-display text-xl font-bold uppercase leading-tight tracking-tight text-white">
                {results[0].name}
              </h5>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">{results[0].finish}</p>
              <span className={`${LINK_ARROW} mt-5`}>
                View product <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          ) : (
            <ul className="mt-6 max-h-[430px] space-y-2 overflow-y-auto pr-1" data-testid="finder-results" data-lenis-prevent>
              {results.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/products/${p.slug}`}
                    className="group flex items-center gap-4 border border-line bg-base p-3 transition-colors hover:bg-panel"
                    data-testid={`finder-card-${p.slug}`}
                  >
                    <div className="spec-frame flex h-14 w-14 shrink-0 items-center justify-center border border-line2">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt={`RangeSafe ${p.name} Stove Safety Knob`}
                          className="max-h-12 w-auto object-contain"
                          width="768"
                          height="1024"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-sm font-bold uppercase tracking-tight text-white">{p.name}</p>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-mute">
                        {p.size} · {p.locking ? 'Ultimate locking' : 'Classic'} · {p.finish}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-mute transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {anySelection && results.length === 0 && (
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
              No models match — adjust a choice.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
